export const INVOICE_STATUS = {
  DRAFT: "draft",
  UNPAID: "unpaid",
  PAID: "paid",
  VOID: "void",
} as const;

export type InvoiceStatus =
  (typeof INVOICE_STATUS)[keyof typeof INVOICE_STATUS];

export const INVOICE_REVIEW_STATE = {
  APPLIED: "applied",
  HELD: "held",
} as const;

export type InvoiceReviewState =
  (typeof INVOICE_REVIEW_STATE)[keyof typeof INVOICE_REVIEW_STATE];

export const INVOICE_SOURCE = {
  MANUAL: "manual",
  CRM: "crm",
} as const;

export type InvoiceSource =
  (typeof INVOICE_SOURCE)[keyof typeof INVOICE_SOURCE];

export type ClinicInvoice = {
  id: string;
  /** Null until an appointment claims a CRM-imported invoice. */
  appointmentId: string | null;
  contactId: string;
  /** Our own sequential number; `externalCode` is the CRM's reference. */
  code: string;
  externalCode: string | null;
  source: InvoiceSource;
  /** Which CRM it came through; null on a manual row. */
  integrationId: string | null;
  invoiceDate: string | null;
  paymentType: string | null;
  createdByCrmUser: string | null;
  /** The workspace user the CRM name matched, when it matched exactly one. */
  matchedUserId: string | null;
  /**
   * Strings because the columns are numeric(12,2); a float would round money.
   * The breakdown is null on a manual invoice and on the webhook path, where
   * only a gross arrives. Where present, total = subtotal - discount + vat.
   */
  subtotal: string | null;
  discount: string | null;
  vat: string | null;
  total: string;
  /**
   * Never asked for: a recorded invoice is money that changed hands. Still read
   * rather than assumed, because the CRM can void one after the fact.
   */
  status: InvoiceStatus;
  /**
   * `held` is a CRM figure that looked like a duplicate of one already
   * recorded - on the record for an admin to accept or void, but not in the
   * appointment value until they do.
   */
  reviewState: InvoiceReviewState;
  paidAt: string | null;
  notes: string | null;
  createdByUserId: string | null;
  /**
   * Whether the viewer may still change or delete it, decided by the server.
   * Only a visit's billing sets it.
   */
  editable?: boolean;
  organizationId: string;
  workspaceId: string;
  createdAt: string;
  updatedAt: string | null;
};

export type CreateInvoicePayload = {
  appointmentId: string;
  total: number;
  externalCode?: string;
  paidAt?: string;
  notes?: string;
  /** Resends a total the server flagged as a duplicate, on purpose. */
  confirmDuplicate?: boolean;
};

export type UpdateInvoicePayload = Partial<
  Omit<CreateInvoicePayload, "appointmentId">
>;

export const INVOICE_ERROR_CODE = {
  DUPLICATE_INVOICE: "duplicate_invoice",
} as const;

/** The 409 body the API returns when a total is already on the appointment. */
export type DuplicateInvoiceConflict = {
  code: string;
  message: string;
  duplicates: ClinicInvoice[];
  /**
   * Whether the server will accept this total resent with `confirmDuplicate`.
   * It decides, not us - false means no override is offered at all.
   */
  addAnywayAllowed: boolean;
};

/**
 * A duplicate is a question, not a failure, so it comes back through `data`.
 * That leaves `isError` meaning what it says: the save actually broke.
 */
export type SaveInvoiceResult =
  | { status: "saved"; invoice: ClinicInvoice }
  | { status: "duplicate"; conflict: DuplicateInvoiceConflict };
